import React, { useEffect, useState, Fragment, useCallback, useRef } from 'react';
import Button from '../../../../shared/button/simpleButton/Button';
import { withTranslation, Trans } from '../../../../i18n';
import { SwiperSlide, Swiper } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import Link from 'next/link';
import LinksProvider from '../../../../services/http/LinksProvider';
import Spinner from '../../../../shared/Spinner/Spinner';
import css from './HomeHeader.module.scss';
import PromoCodesService from '@/services/domain/PromoCodesService';
import PromoCodeBanner from '@/pages/home/components/PromoCodeBanner/PromoCodeBanner';
import Image from "next/image";
import useCheckMobileScreen from 'hooks/useCheckMobileScreen';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';

const namespacesRequired = ['home'];

const HomeHeader = ({ t, activeList, isFetching, pagesData }) => {
  const [windowWidth, setWindowWidth] = useState(null);
  const [bannerPromoCode, setBannerPromoCode] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const headerRef = useRef(null);
  const advantages = ["voyage", "partners", "bonus"];

  const isMobile = useCheckMobileScreen();

  const handlePageResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    // Set initial width
    handlePageResize();

    // Add resize listener
    window.addEventListener('resize', handlePageResize);

    // Load promo code if available
    if (pagesData?.promoCodeBanner) {
      PromoCodesService.get(pagesData.promoCodeBanner.reference)
        .then(setBannerPromoCode);
    }

    // Setup intersection observer with higher threshold
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px'
      }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handlePageResize);
      observer.disconnect();
    };
  }, [handlePageResize, pagesData]);

  const Advantage = ({ image, title, desc }) => {
    return (
      <div className={css.advantage}>
        <Image
          src={image}
          alt={title}
          width={48}
          height={48}
          priority={true}
          fetchPriority="high"
          quality={60}
          loading="eager"
          sizes="(max-width: 768px) 48px, 48px"
          style={{
            transform: image === "/static/images/home/advantages/icon-partners.svg" ? 'scale(1.5)' : 'none',
            maxWidth: "100%",
            height: "auto",
            willChange: 'transform'
          }}
        />
        <div className={css.advantage__content}>
            <h2>
            {title}
          </h2>
          <span>{desc}</span>
        </div>
      </div>
    );
  };

  const MobileAdvantage = () => {
    return (
      <Swiper
        modules={[Pagination]}
        centeredSlides={true}
        pagination={{
          clickable: true,
          dynamicBullets: true,
        }}
        spaceBetween={30}
        slidesPerView={1}
        watchSlidesProgress={true}
        className={css.mobile_swiper}
      >
        {advantages.map((advantage, index) => (
          <SwiperSlide key={index}>
            <div className={css.mobile_container}>
              <Advantage
                image={`/static/images/home/advantages/icon-${advantage}.svg`}
                title={t(`home:advantages.advantage_${index}.title`)}
                desc={t(`home:advantages.advantage_${index}.desc`, { bonus: 5 })}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  return (
    <header className={`${css.header} ${isVisible ? 'visible' : ''}`} ref={headerRef}>
      <div className={css.header_container}>
        <div className={css.header_container__title}>
          <h1 className={css.title}>
            <Trans i18nKey="home:title" components={{ b: <b /> }} />
          </h1>

          <div className={css.btn__container}>
            <a href={!activeList
              ? LinksProvider.getFullRoute(LinksProvider.ROUTES.LIST.CREATE)
              : LinksProvider.getFullRoute(LinksProvider.ROUTES.LIST.LIST, { reference: activeList && activeList.reference })}>
              <Button className={css.btn__primary}>
                {isFetching ? <Spinner /> : (!activeList
                  ? t('common:navbar.create')
                  : t('common:consult-list'))}
              </Button>
            </a>

            <Link href={LinksProvider.get(LinksProvider.ROUTES.LIST.SEARCH_NO_PARAMS)}>
              <Button className={css.btn__light}>
                {t('home:participate')}
              </Button>
            </Link>
          </div>
          {bannerPromoCode && (<PromoCodeBanner code={bannerPromoCode} customClass={css.home_container__promocode} />)}
        </div>

        <div className={css.image_container}>
          <Image
            src="/static/images/home/home-bg.webp"
            alt="suitcase"
            width={isMobile ? 400 : 600}
            height={isMobile ? 400 : 600}
            quality={40}
            loading="eager"
            priority
            layout="intrinsic"
          />
        </div>
      </div>
      <div className={css.advantages_container}>
        <div className={css.advantages}>
          {windowWidth > 874 && (
            <Fragment>
              {advantages.map((advantage, index) => (
                <Advantage
                  key={index}
                  image={`/static/images/home/advantages/icon-${advantage}.svg`}
                  title={t(`home:advantages.advantage_${index}.title`)}
                  desc={t(`home:advantages.advantage_${index}.desc`, { bonus: 5 })}
                />
              ))}
            </Fragment>
          )}

          {windowWidth <= 874 && (
            <Fragment>
              <MobileAdvantage />
            </Fragment>
          )}
        </div>
      </div>
    </header>
  );
};

export default withTranslation(namespacesRequired)(HomeHeader);